<script setup lang="ts">
import { computed, defineProps } from "vue";
import { MonthDayReport } from "@/types/calculators";
import { useI18n } from "vue-i18n";
import { formatDate } from "@/utils/date";
import { useLangStore } from "@/stores/langStore";
import { BADGE_TIER_CLASSES } from "@/constants/tongShu";

const props = defineProps<{
  selectedCard: MonthDayReport;
}>();

const { t } = useI18n();
const langStore = useLangStore();

const doings = computed(() => {
  const res = [];
  const status = props.selectedCard.personal_status;

  const goodContent = status.structures[0] || status.stars[0];
  const warningContent = status.warnings[0];

  if (goodContent) {
    res.push({
      content: `✓ ${goodContent}`,
      classes: "text-success font-semibold",
    });
  }

  if (warningContent) {
    res.push({
      content: `✕ ${warningContent}`,
      classes: "text-error font-semibold",
    });
  }
  if (res.length > 0) {
    return res;
  }

  return [
    {
      content: t(
        "calculators.personal-date.personal_compatibility.influence_neutral"
      ),
      classes: "text-alternative-gray",
    },
  ];
});

const yiDoings = computed(() => {
  const items = props.selectedCard.yi_preview || [];

  if (items.length === 0) return "";

  const list = items.map((item) => `${item.name} (${item.ua})`).join(", ");

  return `宜: ${list}`;
});

const formattedHours = computed(() => {
  const hours = props.selectedCard.best_hours || [];

  return hours
    .map((h) => `${h.branch}时 ${formatHourRange(h.time_std)}`)
    .join(", ");
});

function formatHourRange(rangeStr: string): string {
  if (!rangeStr) return "";

  const startTime = rangeStr.split("-")[0].trim();
  return `(${startTime})`;
}
</script>

<template>
  <div
    id="day-preview-card"
    class="bg-white rounded-2xl border-2 border-active/40 p-4 sm:p-6 shadow-md space-y-4"
  >
    <div
      class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-divider"
    >
      <div>
        <div class="flex flex-wrap items-center gap-2">
          <span
            class="px-2.5 py-0.5 rounded-full text-xs font-bold"
            :class="BADGE_TIER_CLASSES[selectedCard.tier]"
          >
            {{ t(`calculators.personal-date.card-barge.${selectedCard.tier}`) }}
          </span>
          <i18n-t
            keypath="calculators.personal-date.day-index"
            tag="span"
            class="text-xs text-muted"
          >
            <template #score>
              <strong>{{ selectedCard.combined_index }} / 100</strong>
            </template>
          </i18n-t>
        </div>
        <h3
          class="text-lg sm:text-xl font-bold text-brown mt-1 flex flex-wrap items-center gap-2"
        >
          <span>{{ formatDate(selectedCard.date, langStore.current) }}</span>
          <span class="font-caudex text-active">
            ({{ selectedCard.pillars.year }} •
            {{ selectedCard.pillars.month }} • {{ selectedCard.pillars.day }})
          </span>
        </h3>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-active text-white text-xs sm:text-sm font-bold hover:bg-active-dark transition-colors shadow-sm flex items-center justify-center gap-2"
          @click="$emit('openFullReport')"
        >
          <span>
            {{ t("calculators.personal-date.tong_shu.show-full-report") }}
          </span>
          <span>→</span>
        </button>
      </div>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 text-xs">
      <div
        class="p-3.5 rounded-xl bg-secondary border border-divider space-y-1.5"
      >
        <div class="font-bold text-muted uppercase text-[10px]">
          {{ t("calculators.personal-date.tong_shu.title") }}
        </div>
        <div class="font-bold text-brown text-sm">
          {{
            t("calculators.personal-date.tong_shu.ruler", {
              name: `${selectedCard.officer_12.name} (${selectedCard.officer_12.title_ua})`,
              rank: selectedCard.general_status.rank_code,
            })
          }}
        </div>
        <div class="text-alternative-gray">
          <i18n-t
            keypath="calculators.personal-date.tong_shu.yin_day_element"
            tag="span"
          >
            <template #element>
              <strong>{{ selectedCard.day_na_yin }}</strong>
            </template>
          </i18n-t>
          •
          <i18n-t
            keypath="calculators.personal-date.tong_shu.peng_zu"
            tag="span"
          >
            <template #text>
              <strong>{{ selectedCard.peng_zu }}</strong>
            </template>
          </i18n-t>
        </div>
      </div>
      <div
        class="p-3.5 rounded-xl bg-secondary border border-divider space-y-1.5"
      >
        <div class="font-bold text-muted uppercase text-[10px]">
          {{ t("calculators.personal-date.personal_compatibility.title") }}
        </div>

        <div class="font-bold text-brown text-sm">
          {{
            t("calculators.personal-date.personal_compatibility.scoring", {
              percent: selectedCard.personal_status.score,
              gods: `${selectedCard.personal_status.ten_god_stem} / ${selectedCard.personal_status.ten_god_branch}`,
            })
          }}
        </div>

        <div
          v-for="(doing, index) in doings"
          :key="index"
          :class="doing.classes"
        >
          {{ doing.content }}
        </div>
      </div>
      <div class="p-3.5 rounded-xl bg-beige border border-divider space-y-1.5">
        <div class="font-bold text-active uppercase text-[10px]">
          {{ t("calculators.personal-date.favorable_block.title") }}
        </div>
        <div class="text-brown font-semibold">
          {{ yiDoings }}
        </div>
        <div
          v-if="yiDoings === '' && selectedCard.ji_preview.length > 0"
          class="text-error font-semibold"
        >
          {{ selectedCard.ji_preview[0].name }} -
          {{ selectedCard.ji_preview[0].ua }}
        </div>
        <div class="text-alternative-gray pt-1">
          {{ t("calculators.personal-date.favorable_block.best_hours_label") }}
          <strong class="text-brown">
            {{ formattedHours }}
          </strong>
        </div>
      </div>
    </div>
  </div>
</template>
